import type { FunctionComponent } from 'react';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import Image from 'next/image';
import Typography from '@material-ui/core/Typography';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import { InputAdornment } from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircle';

import { FlowType } from '@jerry-serverless/types';

import { StepContainer } from '../components/step_container/StepContainer';
import { NameIcon } from '../components/icon/name_icon/NameIcon';
import { useStepManager } from '../steps';
import { guardedFunc } from '../utils/guarded_function';
import { InfoBox } from '../components/info_box/InfoBox';
import { StepNavType } from '../components/step_container/components/step_nav/type';
import LogosImage from '../components/garage_only/assets/toolbox.png';
import { trackEvent } from '../utils/analytics';
import { ZIP_LENGTH, useValidateZipCode } from '../apollo/graphql/get_zipcode_info.query';
import { MobileButtonWrapper } from '../components/mobile_button_wrapper/MobileButtonWrapper';

const STEP_NAME = 'garageonlystart';

/**
 * GarageOnlyStart
 */
const UnstyledGarageOnlyStart: FunctionComponent<{ className?: string }> = ({ className }) => {
  const {
    appData,
    direction,
    stepIdx,
    stepCnt,
    stepIndicatorData,
    goNext: origGoNext,
    canGoNext,
    goBack: origGoBack,
    canGoBack,
  } = useStepManager(STEP_NAME);
  const goNext = useMemo(() => guardedFunc(origGoNext, canGoNext), [origGoNext, canGoNext]);
  const goBack = useMemo(() => guardedFunc(origGoBack, canGoBack), [origGoBack, canGoBack]);
  const canNotGoBack =
    appData.selectedService?.garage || appData.selectedService?.dwep || appData.selectedService?.refi;

  const [zip, setZip] = useState(appData.zip || '');
  const [errText, setErrText] = useState<string | null>(null);
  const [checking, validateZipCode] = useValidateZipCode();
  const typingTimer = useRef<NodeJS.Timeout | null>(null);

  const label = useMemo(() => (checking ? 'Validating ZIP Code...' : 'Enter Your ZIP Code'), [checking]);
  const continueDisabled = useMemo(
    () => zip?.length < ZIP_LENGTH || checking || !!errText,
    [checking, errText, zip?.length]
  );

  const onValidateZipCode = useCallback(
    async (value: string) => {
      if (checking) {
        return;
      }
      const checkResult = await validateZipCode(value);
      if (checkResult !== true) {
        setErrText(checkResult || '');
      } else {
        setErrText(null);
      }
    },
    [checking, setErrText, validateZipCode]
  );

  const onContinue = useCallback(async () => {
    void trackEvent('GarageOnlyStart', { eventLabel: 'Input ZIP', eventMetadata: { zip } });
    await origGoNext({ ...appData, zip });
  }, [origGoNext, appData, zip]);

  // Track on page viewed, useEffect.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      void trackEvent('GarageOnlyStart', {
        eventLabel: 'Viewed',
        eventMetadata: {
          refer: document.referrer,
          url: window.location.href,
        },
      });
    }
  }, []);

  return (
    <StepContainer
      route={STEP_NAME}
      direction={direction}
      goNext={!checking && !errText ? goNext : undefined}
      goBack={canNotGoBack ? undefined : goBack}
      stepIdx={stepIdx}
      stepCnt={stepCnt}
      stepIndicatorData={stepIndicatorData}
      isMobile={appData.isMobileAgent}
      icon={NameIcon}
      className={className}
      iconText={
        <Box mb={2}>
          <Image alt="logos" height={100} width={100} src={LogosImage} />
          {appData.flowType !== FlowType.RepairShop && (
            <Typography>
              <span className="highlight">Welcome!</span> We’ve got you covered - we can help you estimate the cost for
              385 common repair services.
            </Typography>
          )}
        </Box>
      }
      title="Where would you like to get your car repaired?"
      stepNavType={StepNavType.GarageOnly}
      showTrustViewCard={true}
      showTrustElement={true}
      showPrivacyPolicyCard={false}
    >
      <form>
        <TextField
          variant="filled"
          id="zipInput"
          label={label}
          InputLabelProps={{
            shrink: true,
          }}
          fullWidth
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <InputAdornment position="end">
                {!continueDisabled ? <CheckCircle fontSize="small" color="primary" /> : ''}
              </InputAdornment>
            ),
          }}
          inputProps={{
            maxLength: 5,
            pattern: '^[0-9]+$',
            inputMode: 'numeric',
            'aria-label': 'Enter your 5-digit ZIP code',
            'aria-describedby': errText ? 'zip-error' : undefined,
            autoComplete: 'postal-code',
          }}
          value={zip.replace(/[^a-z0-9A-Z]/g, '')}
          onChange={async (event) => {
            const zip = event.target.value.replace(/[^0-9]/g, '');
            setZip(zip);
            typingTimer.current && clearTimeout(typingTimer.current);
            if (zip.length === ZIP_LENGTH) {
              setErrText(null);
              await onValidateZipCode(zip);
              return;
            }
            typingTimer.current = setTimeout(() => {
              setErrText(`ZIP code should have ${ZIP_LENGTH} digits`);
            }, 1000);
          }}
          disabled={checking}
        />

        <Box mt={2} alignSelf="stretch">
          <MobileButtonWrapper>
            <Button
              id="nameSubmit"
              color="primary"
              variant="contained"
              disableElevation
              fullWidth
              disabled={continueDisabled}
              onClick={onContinue}
            >
              Continue
            </Button>
          </MobileButtonWrapper>
        </Box>

        {!!errText && (
          <Box mt={2}>
            <Alert severity="error">{errText}</Alert>
          </Box>
        )}

        <InfoBox mt={2}>
          {appData.flowType === FlowType.RepairShop
            ? "We'll get quotes from shops near you. Your personal information will be kept confidential."
            : 'Repair costs often vary by location – your zip code helps us get you the right estimate.'}
        </InfoBox>
      </form>
    </StepContainer>
  );
};

export default styled(UnstyledGarageOnlyStart)`
  & .MuiFilledInput-root {
    border-radius: 0.7rem;
    background-color: white;
    border: 1px solid #d9e0e9;
  }
  .highlight {
    font-weight: bold;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;
